import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isTokenExpired as isJwtTokenExpired } from '@ibk/shared-auth';
import { attemptTokenRefresh, persistor, resetSessionExpiredByInactivity, selectAccessToken, selectIsSessionExpiredByInactivity, selectIsTokenExpired, SESSION_MAINTENANCE_EVENT, setSessionExpiredByInactivity, store, useAppDispatch, } from '@ibk/shared-config';
import { localStorageService, sessionStorageService, StorageKeys } from '@ibk/shared-utils';
import { MaintenanceModal } from '../MaintenanceModal';
import { SessionExpiredModal } from '../SessionExpiredModal';
const { AuthToken } = StorageKeys;
const TOKEN_REFRESH_THROTTLE_MS = 3000;
const defaultNavigate = (path) => {
    if (typeof window !== 'undefined') {
        window.location.href = path;
    }
};
const SessionTimeoutContext = createContext(null);
export const useSessionTimeoutContext = () => {
    const context = useContext(SessionTimeoutContext);
    if (!context) {
        throw new Error('useSessionTimeoutContext must be used within a SessionTimeoutProvider');
    }
    return context;
};
export const SessionTimeoutProvider = ({ children, timeoutMinutes = 15, enabled = true, onSessionExpired, onUserActivity, modalClassName, modalStyle, navigate = defaultNavigate, }) => {
    const dispatch = useAppDispatch();
    const [timeRemaining, setTimeRemaining] = useState(timeoutMinutes * 60);
    const [isSessionExpired, setIsSessionExpired] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
    const intervalRef = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const lastRefreshAttemptRef = useRef(0);
    const refreshInProgressRef = useRef(false);
    const tryRefreshTokenOnActivity = useCallback(async () => {
        const currentState = store.getState();
        const accessToken = selectAccessToken(currentState);
        const isTokenExpiredByState = selectIsTokenExpired(currentState);
        const isSessionExpiredByInactivity = selectIsSessionExpiredByInactivity(currentState);
        const isTokenExpiredByJwt = accessToken ? isJwtTokenExpired(accessToken) : false;
        if (isSessionExpiredByInactivity) {
            return;
        }
        if (!isTokenExpiredByState || !isTokenExpiredByJwt) {
            return;
        }
        if (refreshInProgressRef.current) {
            return;
        }
        const now = Date.now();
        if (now - lastRefreshAttemptRef.current < TOKEN_REFRESH_THROTTLE_MS) {
            return;
        }
        lastRefreshAttemptRef.current = now;
        refreshInProgressRef.current = true;
        try {
            await attemptTokenRefresh({
                dispatch,
                getState: () => store.getState(),
            });
        }
        finally {
            refreshInProgressRef.current = false;
        }
    }, [dispatch]);
    const resetTimeout = useCallback(() => {
        lastActivityRef.current = Date.now();
        setTimeRemaining(timeoutMinutes * 60);
        setIsSessionExpired(false);
        setIsActive(true);
        dispatch(resetSessionExpiredByInactivity());
    }, [timeoutMinutes, dispatch]);
    const triggerSessionExpiration = useCallback(async () => {
        localStorageService.removeItem(AuthToken);
        sessionStorageService.clear();
        try {
            await persistor.purge();
        }
        catch (error) {
            console.error('Failed to purge Redux Persist during session expiration:', error);
        }
        setIsSessionExpired(true);
        setShowModal(true);
        dispatch(setSessionExpiredByInactivity());
        onSessionExpired?.();
    }, [onSessionExpired, dispatch]);
    const triggerSessionMaintenance = useCallback(async () => {
        localStorageService.removeItem(AuthToken);
        sessionStorageService.clear();
        try {
            await persistor.purge();
        }
        catch (error) {
            console.error('Failed to purge Redux Persist during session maintenance:', error);
        }
        setShowMaintenanceModal(true);
    }, []);
    useEffect(() => {
        if (!enabled) {
            return;
        }
        const checkTimeout = () => {
            const now = Date.now();
            const timeDiff = Math.floor((now - lastActivityRef.current) / 1000);
            const remaining = Math.max(0, timeoutMinutes * 60 - timeDiff);
            setTimeRemaining(remaining);
            if (remaining <= 0) {
                triggerSessionExpiration();
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }
            }
        };
        intervalRef.current = window.setInterval(checkTimeout, 1000);
        setIsActive(true);
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [enabled, timeoutMinutes, triggerSessionExpiration]);
    useEffect(() => {
        if (!enabled)
            return;
        const handleActivity = () => {
            resetTimeout();
            onUserActivity?.();
            tryRefreshTokenOnActivity();
        };
        const events = ['mousedown', 'keypress', 'click', 'focus', 'touchstart', 'scroll'];
        events.forEach(event => {
            document.addEventListener(event, handleActivity, true);
        });
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleActivity, true);
            });
        };
    }, [enabled, resetTimeout, onUserActivity, tryRefreshTokenOnActivity]);
    useEffect(() => {
        if (!enabled)
            return;
        const handleResetFromRefreshToken = () => {
            resetTimeout();
        };
        window.addEventListener('reset-session-timeout', handleResetFromRefreshToken);
        return () => {
            window.removeEventListener('reset-session-timeout', handleResetFromRefreshToken);
        };
    }, [enabled, resetTimeout]);
    useEffect(() => {
        if (!enabled)
            return;
        const handleForceSessionExpiration = () => {
            triggerSessionExpiration();
        };
        window.addEventListener('force-session-expiration', handleForceSessionExpiration);
        return () => {
            window.removeEventListener('force-session-expiration', handleForceSessionExpiration);
        };
    }, [enabled, triggerSessionExpiration]);
    useEffect(() => {
        if (!enabled)
            return;
        window.addEventListener(SESSION_MAINTENANCE_EVENT, triggerSessionMaintenance);
        return () => {
            window.removeEventListener(SESSION_MAINTENANCE_EVENT, triggerSessionMaintenance);
        };
    }, [enabled, triggerSessionMaintenance]);
    const handleCloseMaintenanceModal = () => {
        setShowMaintenanceModal(false);
        navigate('/login');
    };
    const handleCloseModal = () => {
        setShowModal(false);
        navigate('/login');
    };
    const handleNavigateToLogin = () => {
        setShowModal(false);
        navigate('/login');
    };
    const contextValue = useMemo(() => ({
        timeRemaining,
        resetTimeout,
        triggerSessionExpiration,
        isSessionExpired,
        isActive,
    }), [timeRemaining, resetTimeout, triggerSessionExpiration, isSessionExpired, isActive]);
    return (_jsxs(SessionTimeoutContext.Provider, { value: contextValue, children: [children, _jsx(SessionExpiredModal, { isOpen: showModal, onClose: handleCloseModal, onNavigateToLogin: handleNavigateToLogin, className: modalClassName, style: modalStyle }), _jsx(MaintenanceModal, { isOpen: showMaintenanceModal, onClose: handleCloseMaintenanceModal })] }));
};
